import React, {useState, useMemo} from "react";
import styled from "styled-components";
import {GameItems, TECHNOLOGIES, TECHNOLOGY_SECTIONS} from "../State/GameData";
import {useInventory} from "../State/InventoryContext";
import {ItemIcon} from "../SharedComponents/ItemIcon";
import {WirePuzzleModal} from "./WirePuzzleModal";
import {TechnologyDefinition} from "../State/TechnologyData";
import {useCheatKey} from "../Utility/useCheatKey";
import {SearchInput} from "../SharedComponents/SearchInput";

const S = {
    Page: styled.div` padding: 16px; `,
    SearchContainer: styled.div` margin-bottom: 20px; `,
    FilterRow: styled.div` display: flex; gap: 12px; align-items: center; margin-bottom: 12px; `,
    ToggleLabel: styled.label` display: flex; align-items: center; gap: 8px; cursor: pointer; user-select: none; `,
    Grid: styled.div` display: grid; grid-template-columns: repeat(auto-fit, minmax(280px, 1fr)); gap: 16px; `,
    Card: styled.div` border: 1px solid #ced4da; border-radius: 12px; padding: 14px; background-color: #fff; display: flex; flex-direction: column; gap: 8px; `,
    TitleRow: styled.div` display: flex; justify-content: space-between; align-items: center; gap: 8px; `,
    Button: styled.button<{ $disabled: boolean }>`
      padding: 8px 12px; width: 100%; border-radius: 8px; border: 1px solid ${props => props.$disabled ? "#adb5bd" : "#0d6efd"};
      background-color: ${props => props.$disabled ? "#f1f3f5" : "#e7f1ff"}; color: ${props => props.$disabled ? "#6c757d" : "#0d6efd"}; cursor: ${props => props.$disabled ? "not-allowed" : "pointer"}; font-weight: 700; margin-top: 6px;` ,
    CostList: styled.ul` padding-left: 18px; margin: 0; `,
    Subtitle: styled.p` color: #495057; margin: 0; `,
    Badge: styled.span` background: #e7f5ff; color: #1c7ed6; padding: 4px 8px; border-radius: 8px; font-weight: 700; `,
    HelpPanel: styled.div` position: fixed; top: 80px; right: 24px; width: 320px; max-height: 70vh; overflow: auto; background: #fff; border: 1px solid #ced4da; border-radius: 12px; padding: 12px; box-shadow: 0 12px 32px rgba(0,0,0,0.1); z-index: 5; `,
    TreeNode: styled.div` border-left: 3px solid #e2e8f0; padding-left: 8px; margin-bottom: 10px; `,
    Prereq: styled.span` display: inline-flex; align-items: center; gap: 4px; background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 8px; padding: 2px 8px; margin-right: 6px; font-size: 12px; `,
    EmptyState: styled.div` text-align: center; padding: 40px 20px; color: #64748b; font-size: 14px; `,
};

function TechnologyCard({tech, onStart, unlocked, affordable, prereqMet, cheatActive}: { tech: TechnologyDefinition; onStart: () => void; unlocked: boolean; affordable: boolean; prereqMet: boolean; cheatActive: boolean }) {
    return <S.Card>
        <S.TitleRow>
            <div style={{display: "flex", alignItems: "center", gap: 8}}>
                <ItemIcon itemId={tech.iconId} size={28}/>
                <strong>{tech.name}</strong>
            </div>
            <S.Badge>{unlocked ? "Unlocked" : "Locked"}</S.Badge>
        </S.TitleRow>
        <S.Subtitle>{tech.description}</S.Subtitle>
        {tech.reward && <small><strong>Bonus:</strong> {tech.reward}</small>}
        <div>
            <strong>Prerequisites:</strong>
            <div>
                {(tech.prerequisites ?? []).length === 0 ? "None" : (tech.prerequisites ?? []).map(req => (
                    <S.Prereq key={req}><ItemIcon itemId={TECHNOLOGIES[req].iconId} size={18}/> {TECHNOLOGIES[req].name}</S.Prereq>
                ))}
            </div>
        </div>
        <div>
            <strong>Cost:</strong>
            <S.CostList>
                {tech.cost.map(cost => <li key={cost.itemId}>{cost.quantity}x {GameItems[cost.itemId]?.name ?? cost.itemId}</li>)}
            </S.CostList>
        </div>
        <S.Button $disabled={unlocked || !prereqMet || (!affordable && !cheatActive)} onClick={onStart}>
            {unlocked ? "Already researched" : !prereqMet ? "Need prerequisites" : (affordable || cheatActive) ? "Start research" : "Need more science"}
        </S.Button>
    </S.Card>;
}

function HelpTree({onClose}: { onClose: () => void }) {
    return <S.HelpPanel>
        <div style={{display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8}}>
            <strong>Research Tree</strong>
            <button onClick={onClose}>Close</button>
        </div>
        {TECHNOLOGY_SECTIONS.map(section => (
            <div key={section.id}>
                <h5 style={{marginBottom: 4}}>{section.title}</h5>
                {section.chapters.map(chapter => (
                    <S.TreeNode key={chapter.id}>
                        <strong>{chapter.title}</strong>
                        {chapter.technologies.map(tech => (
                            <div key={tech.id} style={{display: "flex", gap: 6, alignItems: "center", marginTop: 6}}>
                                <ItemIcon itemId={tech.iconId} size={22}/>
                                <div>
                                    <div>{tech.name}</div>
                                    {(tech.prerequisites ?? []).length > 0 && <small>Needs {tech.prerequisites?.map(req => TECHNOLOGIES[req].name).join(", ")}</small>}
                                </div>
                            </div>
                        ))}
                    </S.TreeNode>
                ))}
            </div>
        ))}
    </S.HelpPanel>;
}

export function Science() {
    const {hasTechnology, purchaseTechnology, getItemQuantity} = useInventory();
    const [activeTech, setActiveTech] = useState<TechnologyDefinition | null>(null);
    const [showHelp, setShowHelp] = useState(false);
    const [searchTerm, setSearchTerm] = useState<string>("");
    const [showAll, setShowAll] = useState<boolean>(false);
    const cheatActive = useCheatKey("c");

    const handleComplete = () => {
        if (activeTech) {
            purchaseTechnology(activeTech.id);
            setActiveTech(null);
        }
    };

    const checkAffordable = (tech: TechnologyDefinition) => tech.cost.every(cost => getItemQuantity(cost.itemId) >= cost.quantity);
    const checkPrereqs = (tech: TechnologyDefinition) => (tech.prerequisites ?? []).every(id => hasTechnology(id));

    const filteredSections = useMemo(() => {
        return TECHNOLOGY_SECTIONS.map(section => {
            const filteredTechnologies = section.chapters
                .flatMap(chapter => chapter.technologies)
                .filter(tech => {
                    const unlocked = hasTechnology(tech.id);
                    const prereqMet = checkPrereqs(tech);
                    const affordable = checkAffordable(tech);

                    // By default, hide finished and unavailable technologies
                    if (!showAll) {
                        if (unlocked) return false; // Hide finished
                        if (!prereqMet) return false; // Hide those with missing prerequisites
                    }

                    // Filter by search term
                    if (searchTerm.trim()) {
                        const term = searchTerm.toLowerCase();
                        const techName = tech.name.toLowerCase();
                        const techDescription = tech.description.toLowerCase();
                        const techReward = (tech.reward ?? "").toLowerCase();
                        const prereqNames = (tech.prerequisites ?? [])
                            .map(id => TECHNOLOGIES[id].name.toLowerCase())
                            .join(" ");

                        return techName.includes(term) ||
                               techDescription.includes(term) ||
                               techReward.includes(term) ||
                               prereqNames.includes(term);
                    }

                    return true;
                });

            return {
                ...section,
                filteredTechnologies
            };
        }).filter(section => section.filteredTechnologies.length > 0);
    }, [searchTerm, showAll, hasTechnology, checkAffordable, checkPrereqs]);

    const handleStart = (tech: TechnologyDefinition) => {
        if (hasTechnology(tech.id)) return;
        if (cheatActive && checkPrereqs(tech)) {
            purchaseTechnology(tech.id, {ignoreCost: true});
            return;
        }
        if (!checkAffordable(tech) || !checkPrereqs(tech)) return;
        setActiveTech(tech);
    };

    const scienceInventory = {
        basic: getItemQuantity("sciencePack1"),
        intermediate: getItemQuantity("sciencePack2"),
    };

    return <S.Page>
        <div style={{display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8}}>
            <h2 style={{margin: 0}}>Science</h2>
            <button onClick={() => setShowHelp(prev => !prev)}>{showHelp ? "Hide" : "Show"} research tree</button>
        </div>
        <S.Subtitle>Spend science packs to unlock new capabilities. Each technology now requires a wiring mini-game to enable.</S.Subtitle>
        <div style={{display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 12}}>
            <S.Badge><ItemIcon itemId="sciencePack1" size={18}/> Basic Science: {scienceInventory.basic}</S.Badge>
            <S.Badge><ItemIcon itemId="sciencePack2" size={18}/> Intermediate Science: {scienceInventory.intermediate}</S.Badge>
            {cheatActive && <S.Badge>Cheat held: instant unlocks available</S.Badge>}
        </div>
        <S.SearchContainer>
            <SearchInput
                placeholder="Search technologies by name, description, or prerequisites..."
                value={searchTerm}
                onChange={setSearchTerm}
            />
        </S.SearchContainer>
        <S.FilterRow>
            <S.ToggleLabel>
                <input
                    type="checkbox"
                    checked={showAll}
                    onChange={(e) => setShowAll(e.target.checked)}
                />
                Show all (including finished and unavailable)
            </S.ToggleLabel>
        </S.FilterRow>
        {filteredSections.length === 0 ? (
            <S.EmptyState>
                {searchTerm.trim()
                    ? `No technologies found matching "${searchTerm}"`
                    : "No available technologies to research at this time"}
            </S.EmptyState>
        ) : (
            filteredSections.map(section => (
                <div key={section.id}>
                    <h3 style={{marginBottom: 6}}>{section.title}</h3>
                    <S.Grid>
                        {section.filteredTechnologies.map(tech => {
                            const unlocked = hasTechnology(tech.id);
                            const affordable = checkAffordable(tech);
                            const prereqMet = checkPrereqs(tech);
                            return <TechnologyCard key={tech.id} tech={tech} unlocked={unlocked} affordable={affordable} prereqMet={prereqMet} cheatActive={cheatActive} onStart={() => handleStart(tech)}/>;
                        })}
                    </S.Grid>
                </div>
            ))
        )}
        {activeTech && <WirePuzzleModal technology={activeTech} isOpen={!!activeTech} onClose={() => setActiveTech(null)} onComplete={handleComplete}/>}
        {showHelp && <HelpTree onClose={() => setShowHelp(false)}/>}
    </S.Page>;
}
